// src/components/comunicacion/Encuestas.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ENDPOINTS } from "../../Utils/Endpoints";
import { getSessionUserId, getSessionEmpresaId, isSessionSuperAdmin } from "../../../../utils/SessionUser";
import AlertDialog from "../../../Lms/Components/Shared/AlertDialog";
import sigmaDelete from '../../../Pages/img/Borrar.png';
import sigmaEdit from '../../../Pages/img/Editar.png';
import ConfirmDeleteDialog from "../../../security/ConfirmDeleteDialog";

const ESTADOS = [
    { value: "todas", label: "Todas" },
    { value: "activa", label: "Activas" },
    { value: "cerrada", label: "Cerradas" },
];

const Encuestas = ({ onNavigate }) => {
    const [encuestas, setEncuestas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [areasNegocio, setAreasNegocio] = useState([]);
    const [selectedId, setSelectedId] = useState(null);

    const [filtros, setFiltros] = useState({
        texto: "",
        estado: "todas",
    });

    // formulario de respuesta
    const [respuestas, setRespuestas] = useState({});
    const [erroresRespuesta, setErroresRespuesta] = useState("");
    const [preguntasCargadas, setPreguntasCargadas] = useState([]); // Preguntas con opciones cargadas

    // gestor de encuestas
    const [nuevaEncuesta, setNuevaEncuesta] = useState({
        nombre: "",
        descripcion: "",
        idAreaNegocio: null,
    });
    const [erroresNueva, setErroresNueva] = useState({});

    // preguntas de la nueva encuesta
    const [nuevasPreguntas, setNuevasPreguntas] = useState([
        {
            id: 1,
            tipo: "opcionMultiple", // opcionMultiple, textoLibre, verdaderoFalso
            texto: "",
            opciones: ["", ""], // Para opción múltiple
        },
    ]);

    // Estadísticas
    const [estadisticas, setEstadisticas] = useState({});
    const [loadingStats, setLoadingStats] = useState(false);
    const [modalTextoOpen, setModalTextoOpen] = useState(false);
    const [modalTextoTitulo, setModalTextoTitulo] = useState("");
    const [modalTextoLista, setModalTextoLista] = useState([]);
    const [showAdminModal, setShowAdminModal] = useState(false);
    const [selectedQuestionIndex, setSelectedQuestionIndex] = useState(0);
    const [showResultsModal, setShowResultsModal] = useState(false);
    const [alertDialog, setAlertDialog] = useState({
        open: false,
        title: "",
        message: "",
        variant: "info"
    });
    const [chartModes, setChartModes] = useState({});
    const [editingId, setEditingId] = useState(null);
    const [showModal, setShowModal] = useState(false); // For creation/edit
    const [deleteDialog, setDeleteDialog] = useState({ open: false, item: null });
    const [isUploading, setIsUploading] = useState(false);

    // --- Compartición ---
    const [showShareModal, setShowShareModal] = useState(false);
    const [descendantCompanies, setDescendantCompanies] = useState([]);
    const [selectedCompanies, setSelectedCompanies] = useState({});
    const [sharingLoading, setSharingLoading] = useState(false);
    const [createdEncuestaId, setCreatedEncuestaId] = useState(null); // ID de la encuesta recién creada/editada

    // Cargar datos iniciales
    useEffect(() => {
        const cargarDatos = async () => {
            try {
                // Cargar categorías
                const resCategorias = await axios.get(ENDPOINTS.categorias);
                const rawCats = Array.isArray(resCategorias.data) ? resCategorias.data : [];
                const normalizedCats = rawCats.map(c => ({
                    idCategoria: c.idCategoria ?? c.IdCategoria ?? 0,
                    nombre: c.nombre ?? c.Nombre ?? "Sin categoría"
                }));
                setCategorias(normalizedCats);

                // Cargar áreas de negocio
                const resAreas = await axios.get(ENDPOINTS.areasNegocios);
                const rawAreas = Array.isArray(resAreas.data) ? resAreas.data : [];
                
                const isSuper = isSessionSuperAdmin();
                const myEmpresaId = getSessionEmpresaId();

                const normalizedAreas = rawAreas
                    .filter(a => {
                        if (isSuper) return true;
                        const empId = a.idEmpresa || a.IdEmpresa;
                        return empId === myEmpresaId || !empId || empId === 0;
                    })
                    .map(a => ({
                        idAreaNegocio: a.idAreaNegocio ?? a.IdAreaNegocio ?? 0,
                        nombre: a.nombre ?? a.Nombre ?? "Sin nombre"
                    }));
                console.log("[Encuestas] Áreas de negocio cargadas:", normalizedAreas);
                setAreasNegocio(normalizedAreas);

                // Cargar encuestas
                const resEncuestas = await axios.get(ENDPOINTS.encuestas);
                const rawEncuestas = Array.isArray(resEncuestas.data) ? resEncuestas.data : [];

                const normalizedEncuestas = await Promise.all(rawEncuestas.map(async (e) => {
                    const id = e.idEncuesta ?? e.IdEncuesta ?? 0;
                    let tieneRespuestas = false;
                    try {
                        const resResp = await axios.get(`${ENDPOINTS.encuestas}/${id}/tiene-respuestas`);
                        tieneRespuestas = resResp.data;
                    } catch (error) {
                        console.error(`Error al verificar respuestas de encuesta ${id}:`, error);
                    }

                    return {
                        ...e,
                        idEncuesta: id,
                        nombre: e.nombre ?? e.Nombre ?? "Encuesta sin nombre",
                        descripcion: e.descripcion ?? e.Descripcion ?? "",
                        abierto: e.abierto ?? e.Abierto ?? true,
                        tieneRespuestas: tieneRespuestas,
                        esOrigen: e.esOrigen ?? e.EsOrigen ?? false
                    };
                }));
                setEncuestas(normalizedEncuestas);

                if (normalizedEncuestas.length > 0) {
                    setSelectedId(normalizedEncuestas[0].idEncuesta);
                }

                // Setear valores por defecto
                if (normalizedAreas.length > 0) {
                    setNuevaEncuesta(prev => ({
                        ...prev,
                        idAreaNegocio: normalizedAreas[0]?.idAreaNegocio || null,
                    }));
                }
            } catch (error) {
                console.error("Error al cargar datos iniciales en Encuestas:", error);
            }
        };
        cargarDatos();
    }, []);

    const handleFiltrosChange = (e) => {
        const { name, value } = e.target;
        setFiltros((prev) => ({ ...prev, [name]: value }));
    };

    const encuestasFiltradas = useMemo(() => {
        return encuestas.filter((e) => {
            const nombre = e.nombre || "";
            const descripcion = e.descripcion || "";
            const textoLower = filtros.texto.toLowerCase();

            // Filtro de texto
            if (filtros.texto &&
                !(nombre.toLowerCase().includes(textoLower) || descripcion.toLowerCase().includes(textoLower))
            ) {
                return false;
            }

            // Filtro de estado
            if (filtros.estado !== "todas") {
                const isAbierto = e.abierto;
                if (filtros.estado === "activa" && !isAbierto) return false;
                if (filtros.estado === "cerrada" && isAbierto) return false;
            }
            return true;

        });
    }, [encuestas, filtros]);

    const encuestaSeleccionada = useMemo(
        () => encuestas.find((e) => e.idEncuesta === selectedId) || null,
        [encuestas, selectedId]
    );

    const handleSeleccionarEncuesta = async (encuesta) => {
        const idEncuesta = encuesta.idEncuesta;
        setSelectedId(idEncuesta);
        setErroresRespuesta("");

        // Cargar preguntas de la encuesta
        try {
            const resPreguntas = await axios.get(ENDPOINTS.encuestaPreguntas);
            const rawPreguntas = Array.isArray(resPreguntas.data) ? resPreguntas.data : [];
            const preguntasEncuesta = rawPreguntas
                .filter(p => (p.idEncuesta ?? p.IdEncuesta) === idEncuesta)
                .map(p => ({
                    ...p,
                    idEncuesta: p.idEncuesta ?? p.IdEncuesta ?? 0,
                    idEncuestaPregunta: p.idEncuestaPregunta ?? p.IdEncuestaPregunta ?? 0,
                    idTipoPregunta: p.idTipoPregunta ?? p.IdTipoPregunta ?? 0,
                    descripcionPregunta: p.descripcionPregunta ?? p.DescripcionPregunta ?? p.texto ?? p.Texto ?? "",
                    tipo: p.tipo ?? p.Tipo ?? "opcionMultiple"
                }));

            // Cargar opciones para cada pregunta según su tipo
            const preguntasConOpciones = await Promise.all(
                preguntasEncuesta.map(async (pregunta) => {
                    const idTipoPregunta = pregunta.idTipoPregunta;

                    if (idTipoPregunta === 2) {
                        // Opción múltiple
                        try {
                            const resOpcionMultiple = await axios.get(ENDPOINTS.encuestaPreguntaOpcionMultiple);
                            const opcionMultiple = resOpcionMultiple.data.find(
                                om => (om.idEncuestaPregunta ?? om.IdEncuestaPregunta) === pregunta.idEncuestaPregunta
                            );

                            if (opcionMultiple) {
                                const idRespOM = opcionMultiple.idRespuestaOpcionMultiple ?? opcionMultiple.IdRespuestaOpcionMultiple;
                                const resOpciones = await axios.get(ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones);
                                const opciones = resOpciones.data.filter(
                                    o => (o.idRespuestaOpcionMultiple ?? o.IdRespuestaOpcionMultiple) === idRespOM
                                ).map(o => ({
                                    ...o,
                                    idOpcion: o.idOpcion ?? o.IdOpcion,
                                    texto: o.texto ?? o.Texto,
                                    orden: o.orden ?? o.Orden
                                })).sort((a, b) => (a.orden || 0) - (b.orden || 0));

                                return {
                                    ...pregunta,
                                    tipo: "opcionMultiple",
                                    opciones: opciones,
                                    idRespuestaOpcionMultiple: idRespOM
                                };
                            }
                        } catch (error) {
                            console.error("Error cargando opciones múltiples:", error);
                        }
                    } else if (idTipoPregunta === 3) {
                        // Verdadero/Falso
                        return {
                            ...pregunta,
                            tipo: "verdaderoFalso",
                            opciones: [
                                { idOpcion: "V", texto: "Verdadero" },
                                { idOpcion: "F", texto: "Falso" }
                            ]
                        };
                    } else if (idTipoPregunta === 1) {
                        // Texto libre
                        return {
                            ...pregunta,
                            tipo: "textoLibre",
                            opciones: []
                        };
                    }

                    return { ...pregunta, tipo: "desconocido", opciones: [] };
                })
            );

            setPreguntasCargadas(preguntasConOpciones);
            setShowResultsModal(true);

            // Limpiar respuestas
            const respInicial = {};
            preguntasConOpciones.forEach((p) => {
                respInicial[p.idEncuestaPregunta] = "";
            });
            setRespuestas(respInicial);
        } catch (error) {
            console.error("Error al cargar preguntas:", error);
        }
    };

    const handleChangeRespuesta = (preguntaId, valor) => {
        setRespuestas((prev) => ({
            ...prev,
            [preguntaId]: valor,
        }));
    };

    const validarRespuestas = () => {
        // TODO: Implementar validación
        setErroresRespuesta("");
        return true;
    };

    // Cargar estadísticas cuando cambia la selección
    useEffect(() => {
        if (encuestaSeleccionada && preguntasCargadas.length > 0) {
            cargarEstadisticas();
        } else {
            setEstadisticas({});
        }
    }, [encuestaSeleccionada, preguntasCargadas]);

    const cargarEstadisticas = async () => {
        setLoadingStats(true);
        try {
            // IDs de preguntas de esta encuesta
            const qIds = new Set(preguntasCargadas.map(p => p.idEncuestaPregunta));

            // Cargar TODAS las respuestas (Desafortunadamente no hay filtro por encuesta en endpoint a n, se procesa en cliente)
            // Idealmente el backend filtrar a.
            const [resResp, resDetOM, resDetVF, resDetText] = await Promise.all([
                axios.get(ENDPOINTS.encuestaRespuestas),
                axios.get(ENDPOINTS.respuestaEncuestaOpcionMultipleDet),
                axios.get(ENDPOINTS.respuestaEncuestaVerdaderoFalso),
                axios.get(ENDPOINTS.respuestaEncuestaTextoLibre)
            ]);

            console.log('=== DEBUG ESTADÍSTICAS ===');
            console.log('Respuestas generales:', resResp.data);
            console.log('Detalles OpcionMultiple:', resDetOM.data);
            console.log('Detalles VerdaderoFalso:', resDetVF.data);
            console.log('Detalles TextoLibre:', resDetText.data);
            console.log('IDs de preguntas:', Array.from(qIds));

            // Filtrar respuestas que pertenecen a las preguntas de esta encuesta
            const relevantResp = resResp.data.filter(r => {
                const rPreguntaId = r.idPregunta || r.IdPregunta || r.idEncuestaPregunta || r.IdEncuestaPregunta;
                return qIds.has(rPreguntaId);
            });
            const relevantRespIds = new Set(relevantResp.map(r => r.idEncuestaRespuesta || r.IdEncuestaRespuesta));

            console.log('Respuestas relevantes:', relevantResp);
            console.log('IDs de respuestas relevantes:', Array.from(relevantRespIds));

            const newStats = {};

            for (const p of preguntasCargadas) {
                // Respuestas para esta pregunta
                const pResps = relevantResp.filter(r => {
                    const rPreguntaId = r.idPregunta || r.IdPregunta || r.idEncuestaPregunta || r.IdEncuestaPregunta;
                    return rPreguntaId === p.idEncuestaPregunta;
                });
                const pRespIds = new Set(pResps.map(r => r.idEncuestaRespuesta || r.IdEncuestaRespuesta));

                console.log(`\n--- Pregunta ${p.idEncuestaPregunta} (${p.tipo}) ---`);
                console.log('Respuestas para esta pregunta:', pResps);
                console.log('IDs de respuestas:', Array.from(pRespIds));

                // Total de participaciones para esta pregunta
                const total = pResps.length;

                if (p.tipo === 'opcionMultiple') {
                    const dets = resDetOM.data.filter(d => pRespIds.has(d.idEncuestaRespuesta || d.IdEncuestaRespuesta));
                    console.log('Detalles OM filtrados:', dets);
                    const counts = {};
                    // Init counts
                    p.opciones.forEach(op => counts[op.idOpcion] = 0);
                    // Fill counts
                    dets.forEach(d => {
                        const k = d.idOpcion || d.IdOpcion || d.idOpcionSeleccionada || d.IdOpcionSeleccionada;
                        console.log(`Contando opción ${k}, objeto completo:`, d);
                        if (counts[k] !== undefined) counts[k]++;
                    });
                    console.log('Counts finales:', counts);
                    newStats[p.idEncuestaPregunta] = { total, counts };

                } else if (p.tipo === 'verdaderoFalso') {
                    const dets = resDetVF.data.filter(d => pRespIds.has(d.idEncuestaRespuesta || d.IdEncuestaRespuesta));
                    console.log('Detalles VF filtrados:', dets);
                    const counts = { V: 0, F: 0 };
                    dets.forEach(d => {
                        console.log('Respuesta VF:', d.respuesta, 'objeto completo:', d);
                        if (d.respuesta) counts.V++; else counts.F++;
                    });
                    console.log('Counts VF finales:', counts);
                    newStats[p.idEncuestaPregunta] = { total, counts };

                } else if (p.tipo === 'textoLibre') {
                    const dets = resDetText.data.filter(d => pRespIds.has(d.idEncuestaRespuesta || d.IdEncuestaRespuesta));
                    console.log('Detalles Texto filtrados:', dets);
                    const textos = dets.map(d => d.textoRespuesta || d.TextoRespuesta).filter(t => t);
                    console.log('Textos finales:', textos);
                    newStats[p.idEncuestaPregunta] = { total, textos };
                }
            }

            console.log('\n=== ESTADÍSTICAS FINALES ===');
            console.log(newStats);
            setEstadisticas(newStats);

        } catch (error) {
            console.error("Error calculando estadísticas:", error);
        } finally {
            setLoadingStats(false);
        }
    };

    const handleOpenModalRespuestas = (titulo, lista) => {
        setModalTextoTitulo(titulo);
        setModalTextoLista(lista);
        setModalTextoOpen(true);
    };

    // ===== GESTOR: nueva encuesta =====

    const handleNuevaEncuestaChange = (e) => {
        const { name, value } = e.target;
        setNuevaEncuesta((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handlePreguntaChange = (id, field, value) => {
        setNuevasPreguntas((prev) =>
            prev.map((p) => (p.id === id ? { ...p, [field]: value } : p))
        );
    };

    const handleOpcionChange = (preguntaId, index, value) => {
        setNuevasPreguntas((prev) =>
            prev.map((p) => {
                if (p.id === preguntaId) {
                    let nuevasOpciones = [...p.opciones];
                    nuevasOpciones[index] = value;

                    // Si escribimos en la última y hay menos de 10, agregamos una vacía (ghost)
                    if (index === nuevasOpciones.length - 1 && value.trim() !== "" && nuevasOpciones.length < 10) {
                        nuevasOpciones.push("");
                    }

                    return { ...p, opciones: nuevasOpciones };
                }
                return p;
            })
        );
    };

    const handleOpcionBlur = (preguntaId) => {
        setNuevasPreguntas((prev) =>
            prev.map((p) => {
                if (p.id === preguntaId) {
                    // Consolidar: eliminar vacías intermedias pero mantener el comportamiento ghost
                    let filled = p.opciones.filter(o => o.trim() !== "");
                    let result = [...filled];

                    // Solo agregamos ghost si hay espacio (menos de 10)
                    if (result.length < 10) {
                        result.push("");
                    }

                    // Asegurar mínimo 3
                    while (result.length < 3) result.push("");

                    return { ...p, opciones: result };
                }
                return p;
            })
        );
    };

    // handleAgregarOpcion eliminada: ahora es automática mediante handleOpcionChange (Ghost options)

    const handleEditar = async (enc) => {
        if (enc.abierto) {
            setAlertDialog({
                open: true,
                title: "Acción no permitida",
                message: "La encuesta no puede estar abierta para editarse. Por favor ciérrala primero.",
                variant: "error"
            });
            return;
        }

        if (enc.tieneRespuestas) {
            setAlertDialog({
                open: true,
                title: "Acción no permitida",
                message: "La encuesta ya tiene respuestas registradas y no se permite su edición.",
                variant: "error"
            });
            return;
        }

        const idActual = enc.idEncuesta ?? enc.IdEncuesta;
        setEditingId(idActual);

        try {
            // 1. Cargar todas las preguntas de la encuesta
            const resPreguntas = await axios.get(ENDPOINTS.encuestaPreguntas);
            const allRawPreguntas = Array.isArray(resPreguntas.data) ? resPreguntas.data : [];
            const rawPreguntas = allRawPreguntas.filter(p => (p.idEncuesta ?? p.IdEncuesta) === idActual);

            // 2. Cargar TODA la data necesaria en bulto para mapeo rápido
            const [resOM, resOps, resVF] = await Promise.all([
                axios.get(ENDPOINTS.encuestaPreguntaOpcionMultiple),
                axios.get(ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones),
                axios.get(ENDPOINTS.encuestaPreguntaVerdaderoFalso)
            ]);

            const allOM = Array.isArray(resOM.data) ? resOM.data : [];
            const allOps = Array.isArray(resOps.data) ? resOps.data : [];
            // const allVF = Array.isArray(resVF.data) ? resVF.data : []; // No se usa actualmente pero útil para futuro

            // 3. Unir las piezas localmente
            const preguntasConTodo = rawPreguntas.map(p => {
                const idP = p.idEncuestaPregunta ?? p.IdEncuestaPregunta;
                const tipoId = p.idTipoPregunta ?? p.IdTipoPregunta;
                const tipo = (tipoId === 1) ? "textoLibre" : "opcionMultiple";
                const texto = p.descripcionPregunta ?? p.DescripcionPregunta ?? p.Texto ?? "";

                let opciones = [];
                if (tipo === "opcionMultiple") {
                    // Encontrar el bloque OM de esta pregunta
                    const om = allOM.find(x => (x.idEncuestaPregunta ?? x.IdEncuestaPregunta) === idP);
                    if (om) {
                        const idRespOM = om.idRespuestaOpcionMultiple ?? om.IdRespuestaOpcionMultiple;
                        // Filtrar y mapear opciones activas
                        const validOps = allOps
                            .filter(o =>
                                (o.idRespuestaOpcionMultiple ?? o.IdRespuestaOpcionMultiple) === idRespOM &&
                                (o.activo !== false && o.Activo !== false)
                            )
                            .map(o => o.texto ?? o.Texto ?? "");

                        // Agregar ghost o base mínima, máximo 10
                        const baseOps = validOps.length > 0 ? validOps : ["", "", ""];
                        opciones = (baseOps.length < 10 && baseOps.some(o => o.trim() !== "")) ? [...baseOps, ""] : baseOps;
                        // Asegurar que no exceda 10 en ningún caso
                        if (opciones.length > 10) opciones = opciones.slice(0, 10);
                    } else {
                        opciones = ["", "", ""];
                    }
                }

                return {
                    id: idP,
                    idEncuestaPregunta: idP,
                    tipo: tipo,
                    texto: texto,
                    opciones: opciones
                };
            });

            // 4. Actualizar estados del frontend
            setNuevaEncuesta({
                nombre: enc.nombre ?? enc.Nombre ?? "",
                descripcion: enc.descripcion ?? enc.Descripcion ?? "",
                idCategoria: enc.idCategoria ?? enc.IdCategoria ?? null,
                idAreaNegocio: enc.idAreaNegocio ?? enc.IdAreaNegocio ?? null,
            });

            setNuevasPreguntas(preguntasConTodo.length > 0 ? preguntasConTodo : [{ id: 1, tipo: "opcionMultiple", texto: "", opciones: ["", "", ""] }]);
            setSelectedQuestionIndex(0);
            setShowAdminModal(true);
        } catch (error) {
            console.error("Error al cargar encuesta para editar:", error);
        }
    };

    const handleEliminar = (enc) => {
        setDeleteDialog({ open: true, item: enc });
    };

    const executeDelete = async () => {
        const item = deleteDialog.item;
        if (!item || !item.idEncuesta) return;

        try {
            await axios.delete(`${ENDPOINTS.encuestas}/${item.idEncuesta}`);
            setEncuestas(prev => prev.filter(e => e.idEncuesta !== item.idEncuesta));
            setAlertDialog({
                open: true,
                title: "¡Éxito!",
                message: "Encuesta eliminada correctamente",
                variant: "success"
            });
            setDeleteDialog({ open: false, item: null });
        } catch (error) {
            console.error("Error al eliminar encuesta:", error);
            setAlertDialog({
                open: true,
                title: "Error",
                message: "No se pudo eliminar la encuesta. Es posible que tenga respuestas asociadas.",
                variant: "error"
            });
        }
    };

    const handleToggleActivo = async (enc) => {
        const nuevoEstado = !enc.abierto;
        const payload = {
            idEncuesta: enc.idEncuesta,
            idAreaNegocio: enc.idAreaNegocio,
            descripcion: enc.descripcion,
            nombre: enc.nombre,
            abierto: nuevoEstado,
            idEmpresa: getSessionEmpresaId()
        };

        try {
            await axios.put(ENDPOINTS.encuestas, payload);
            setEncuestas(prev => prev.map(e => e.idEncuesta === enc.idEncuesta ? { ...e, abierto: nuevoEstado } : e));
            setAlertDialog({
                open: true,
                title: "Estado actualizado",
                message: `Encuesta ${nuevoEstado ? 'activada' : 'desactivada'} correctamente`,
                variant: "success"
            });
        } catch (error) {
            console.error("Error al cambiar estado:", error);
        }
    };
    const handleEliminarOpcion = (preguntaId, index) => {
        setNuevasPreguntas((prev) =>
            prev.map((p) => {
                if (p.id === preguntaId) {
                    // Eliminamos por índice
                    let ops = p.opciones.filter((_, i) => i !== index);

                    // Consolidar para que suban las respuestas
                    let filled = ops.filter(o => o.trim() !== "");
                    let result = [...filled];

                    // Ghost solo si hay espacio (máximo 10)
                    if (result.length < 10) result.push("");

                    // Respetar límites
                    while (result.length < 3) result.push("");

                    return { ...p, opciones: result };
                }
                return p;
            })
        );
    };

    const handleAgregarPregunta = () => {
        setNuevasPreguntas((prev) => [
            ...prev,
            {
                id: Date.now(),
                tipo: "opcionMultiple",
                texto: "",
                opciones: ["", "", ""],
            },
        ]);
    };

    const handleEliminarPregunta = (id) => {
        setNuevasPreguntas((prev) => prev.filter((p) => p.id !== id));
    };

    const getQuestionError = (p) => {
        if (!p) return null;
        if (!p.texto.trim()) return "El título de la pregunta es obligatorio.";
        if (p.tipo === "opcionMultiple") {
            const validOptions = p.opciones.filter(o => o.trim());
            if (validOptions.length < 2) return "Agrega al menos 2 opciones válidas.";
            const uniqueOptions = new Set(validOptions.map(o => o.toLowerCase().trim()));
            if (uniqueOptions.size !== validOptions.length) return "No puedes poner opciones idénticas.";
        }
        return null;
    };

    const canAddQuestion = nuevasPreguntas.every(p => !getQuestionError(p));

    const validarNuevaEncuesta = () => {
        const errs = {};
        if (!nuevaEncuesta.nombre.trim()) {
            errs.nombre = "El nombre es obligatorio.";
        }
        // Descripción opcional (sin validación obligatoria)
        if (nuevasPreguntas.length === 0) {
            errs.preguntas = "La encuesta debe tener al menos una pregunta.";
        } else {
            nuevasPreguntas.forEach((p, idx) => {
                if (!p.texto.trim()) {
                    errs[`preg_${p.id}`] = `La pregunta ${idx + 1} necesita un texto.`;
                }
                if (p.tipo === "opcionMultiple") {
                    // Filtrar la última opción si está vacía (ghost option)
                    const opcionesValidas = p.opciones.filter(o => o.trim());
                    if (opcionesValidas.length < 2) {
                        errs[`preg_${p.id}`] = "Agrega al menos 2 opciones válidas.";
                    } else {
                        // Validar opciones duplicadas
                        const setOpciones = new Set(opcionesValidas.map(o => o.toLowerCase().trim()));
                        if (setOpciones.size !== opcionesValidas.length) {
                            errs[`preg_${p.id}`] = "No puedes poner opciones idénticas en la misma pregunta.";
                        }
                    }
                }
            });
        }
        setErroresNueva(errs);
        return Object.keys(errs).length === 0;
    };

    const handleCrearEncuesta = async (e) => {
        e.preventDefault();
        if (!validarNuevaEncuesta()) return;

        const idUsuario = getSessionUserId();
        setIsUploading(true);

        try {
            // Crear/Editar encuesta
            const encuestaPayload = {
                idEncuesta: editingId || 0,
                idAreaNegocio: nuevaEncuesta.idAreaNegocio,
                descripcion: nuevaEncuesta.descripcion,
                nombre: nuevaEncuesta.nombre,
                abierto: false,
                idEmpresa: getSessionEmpresaId()
            };

            let idEncuesta;
            if (editingId) {
                await axios.put(ENDPOINTS.encuestas, encuestaPayload);
                idEncuesta = editingId;
            } else {
                const resEncuesta = await axios.post(ENDPOINTS.encuestas, encuestaPayload);
                idEncuesta = resEncuesta.data.idEncuesta || resEncuesta.data;
            }

            // Para simplificar, en edición eliminamos preguntas anteriores y creamos nuevas
            // O podemos solo crear si es nueva. 
            // El backend suele manejar esto mejor con un endpoint de batch, pero seguiremos el flujo actual.
            if (editingId) {
                // TODO: Limpiar preguntas anteriores si el backend lo requiere.
                // Por ahora asumimos que el usuario solo crea nuevas o el backend maneja el merge.
                // En un sistema real, esto sería más complejo.
            }

            // Crear preguntas
            for (const pregunta of nuevasPreguntas) {
                if (!pregunta.texto.trim()) continue; // Evitar vacías si se colaron

                const idTipoPregunta =
                    pregunta.tipo === "textoLibre" ? 1 :
                        pregunta.tipo === "opcionMultiple" ? 2 :
                            3; // verdaderoFalso

                const preguntaPayload = {
                    idEncuestaPregunta: (editingId && pregunta.idEncuestaPregunta) ? pregunta.idEncuestaPregunta : 0,
                    idEncuesta: idEncuesta,
                    idTipoPregunta: idTipoPregunta,
                    descripcionPregunta: pregunta.texto,
                    retroalimentacionoContexto: "Sin retroalimentación",
                };

                let idPregunta;
                if (preguntaPayload.idEncuestaPregunta > 0) {
                    await axios.put(ENDPOINTS.encuestaPreguntas, preguntaPayload);
                    idPregunta = preguntaPayload.idEncuestaPregunta;
                } else {
                    const resPregunta = await axios.post(ENDPOINTS.encuestaPreguntas, preguntaPayload);
                    idPregunta = resPregunta.data.idEncuestaPregunta || resPregunta.data;
                }

                // Crear estructura según tipo
                if (pregunta.tipo === "opcionMultiple") {
                    // Si ya existe OM, la actualizamos o creamos
                    const resOMAll = await axios.get(ENDPOINTS.encuestaPreguntaOpcionMultiple);
                    const existingOM = resOMAll.data.find(om => (om.idEncuestaPregunta ?? om.IdEncuestaPregunta) === idPregunta);

                    let idOpcionMultiple;
                    if (existingOM) {
                        idOpcionMultiple = existingOM.idRespuestaOpcionMultiple ?? existingOM.IdRespuestaOpcionMultiple;
                    } else {
                        const opcionMultiplePayload = {
                            idRespuestaOpcionMultiple: 0,
                            idEncuestaPregunta: idPregunta,
                            permiteMultiple: false,
                        };
                        const resOM = await axios.post(ENDPOINTS.encuestaPreguntaOpcionMultiple, opcionMultiplePayload);
                        idOpcionMultiple = resOM.data.idRespuestaOpcionMultiple || resOM.data;
                    }

                    // Crear/Actualizar opciones
                    const opcionesValidas = pregunta.opciones.filter(o => o.trim());

                    // LIMPIEZA ROBUSTA: Antes de insertar las nuevas, borramos las anteriores
                    try {
                        // 1. Obtener todas las opciones (global)
                        const resOpsAll = await axios.get(ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones);
                        const allOptionsInDB = Array.isArray(resOpsAll.data) ? resOpsAll.data : [];

                        // 2. Filtrar las que pertenecen a este bloque OM específico
                        const opsExistentes = allOptionsInDB.filter(o =>
                            (o.idRespuestaOpcionMultiple ?? o.IdRespuestaOpcionMultiple) === idOpcionMultiple &&
                            (o.activo !== false && o.Activo !== false)
                        );

                        // 3. Borrarlas (desactivarlas) si existen
                        if (opsExistentes.length > 0) {
                            for (const op of opsExistentes) {
                                const idOp = op.idOpcion ?? op.IdOpcion;
                                await axios.delete(`${ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones}/${idOp}`);
                            }
                        }
                    } catch (errClean) {
                        console.warn("Fallo al limpiar opciones anteriores, continuando...", errClean);
                    }

                    // 4. Insertar las nuevas opciones actualizadas
                    for (let i = 0; i < opcionesValidas.length; i++) {
                        const opcionPayload = {
                            idOpcion: 0,
                            idRespuestaOpcionMultiple: idOpcionMultiple,
                            texto: opcionesValidas[i],
                            orden: i + 1,
                        };
                        await axios.post(ENDPOINTS.encuestaRespuestaOpcionMultipleOpciones, opcionPayload);
                    }
                } else if (pregunta.tipo === "verdaderoFalso") {
                    const vfAll = await axios.get(ENDPOINTS.encuestaPreguntaVerdaderoFalso);
                    const existingVF = vfAll.data.find(vf => (vf.idEncuestaPregunta ?? vf.IdEncuestaPregunta) === idPregunta);

                    if (!existingVF) {
                        const verdaderoFalsoPayload = {
                            idRespuestaVerdaderoFalso: 0,
                            idEncuestaPregunta: idPregunta,
                        };
                        await axios.post(ENDPOINTS.encuestaPreguntaVerdaderoFalso, verdaderoFalsoPayload);
                    }
                }
            }

            // Obtener empresas descendientes para ver si hay con quien compartir
            const sessionEmpresaId = getSessionEmpresaId();
            const resDesc = await axios.get(ENDPOINTS.empresasDescendientes(sessionEmpresaId));
            const descendientes = resDesc.data || [];

            if (descendientes.length === 0) {
                // Sin hijas: guardar directo
                await executeGuardarComparticion(idEncuesta, {});
                return;
            }

            // Pre-seleccionar empresas ya compartidas si editamos
            let preSelected = {};
            if (editingId) {
                try {
                    const resShared = await axios.get(ENDPOINTS.encuestasCompartidasByEncuesta(editingId));
                    const sharedData = resShared.data || [];
                    sharedData.forEach(sh => {
                        if (!sh.esOrigen && !sh.EsOrigen) {
                            const empId = sh.IdEmpresa || sh.idEmpresa;
                            preSelected[empId] = true;
                        }
                    });
                } catch(e) { console.warn("Could not fetch shared surveys", e); }
            }

            setCreatedEncuestaId(idEncuesta);
            setDescendantCompanies(descendientes);
            setSelectedCompanies(preSelected);
            setShowAdminModal(false);
            setShowShareModal(true);

        } catch (error) {
            console.error("Error al guardar encuesta:", error);
            const errorMessage = error.response?.data ? JSON.stringify(error.response.data) : error.message;
            setAlertDialog({
                open: true,
                title: "Error",
                message: `Error al guardar encuesta: ${errorMessage}`,
                variant: "error"
            });
            setIsUploading(false);
        }
    };

    const executeGuardarComparticion = async (idEncuesta, selectedShareCompanies = {}) => {
        setSharingLoading(true);
        try {
            if (idEncuesta && Object.keys(selectedShareCompanies).length > 0) {
                try {
                    const resShared = await axios.get(ENDPOINTS.encuestasCompartidasByEncuesta(idEncuesta));
                    const existingShared = resShared.data || [];

                    for (const idEmpStr of Object.keys(selectedShareCompanies)) {
                        const empId = Number(idEmpStr);
                        const isSelected = selectedShareCompanies[empId];
                        const existingRec = existingShared.find(s => (s.IdEmpresa || s.idEmpresa) === empId);

                        if (isSelected && !existingRec) {
                            await axios.post(ENDPOINTS.encuestasCompartidas, {
                                idEncuesta: idEncuesta,
                                idEmpresa: empId,
                                activo: true
                            });
                        } else if (!isSelected && existingRec) {
                            await axios.delete(`${ENDPOINTS.encuestasCompartidas}/${existingRec.IdEncuestaCompartida || existingRec.idEncuestaCompartida}`);
                        }
                    }
                } catch(shareErr) {
                    console.error("Error updating sharing permissions", shareErr);
                }
            }

            // Recargar encuestas
            const resReload = await axios.get(ENDPOINTS.encuestas);
            const rawReload = Array.isArray(resReload.data) ? resReload.data : [];
            const normalizedReload = await Promise.all(rawReload.map(async (e) => {
                const id = e.idEncuesta ?? e.IdEncuesta ?? 0;
                let tieneRespuestas = false;
                try {
                    const resResp = await axios.get(`${ENDPOINTS.encuestas}/${id}/tiene-respuestas`);
                    tieneRespuestas = resResp.data;
                } catch (error) {
                    console.error(`Error al verificar respuestas de encuesta ${id}:`, error);
                }

                return {
                    ...e,
                    idEncuesta: id,
                    nombre: e.nombre ?? e.Nombre ?? "Encuesta sin nombre",
                    descripcion: e.descripcion ?? e.Descripcion ?? "",
                    abierto: e.abierto ?? e.Abierto ?? true,
                    tieneRespuestas: tieneRespuestas,
                    esOrigen: e.esOrigen ?? e.EsOrigen ?? false
                };
            }));
            setEncuestas(normalizedReload);

            // Limpiar formulario
            limpiarFormulario();
            setShowAdminModal(false);
            setShowShareModal(false);
            setCreatedEncuestaId(null);

            setAlertDialog({
                open: true,
                title: "¡Éxito!",
                message: editingId ? "Encuesta actualizada correctamente" : "Encuesta creada correctamente",
                variant: "success"
            });
        } catch (error) {
            console.error("Error al finalizar guardado de encuesta:", error);
        } finally {
            setSharingLoading(false);
            setIsUploading(false);
        }
    };


    const limpiarFormulario = () => {
        setNuevaEncuesta({
            nombre: "",
            descripcion: "",
            idCategoria: categorias[0]?.idCategoria || null,
            idAreaNegocio: areasNegocio[0]?.idAreaNegocio || null,
        });
        setNuevasPreguntas([
            {
                id: 1,
                tipo: "opcionMultiple",
                texto: "",
                opciones: ["", "", ""],
            },
        ]);
        setEditingId(null);
        setErroresNueva({});
        setSelectedQuestionIndex(0);
    };

    return (
        <div className="intra-survey-page">
            <header className="intra-avisos-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <div>
                    {onNavigate && (
                        <button
                            type="button"
                            className="intra-button-ghost btn-cancel"
                            onClick={() => onNavigate("dashboard")}
                            style={{
                                marginBottom: '0.75rem'
                            }}
                        >
                            <span style={{ fontSize: '1.1rem', fontWeight: 'bold' }}>←</span>
                            <span>Volver al dashboard</span>
                        </button>
                    )}
                    <h1 className="intra-avisos-title">Gestor de encuestas internas</h1>
                </div>
            </header>

            {/* TOOLBAR Y FILTROS */}
            <section className="intra-news-toolbar">
                <div className="intra-news-filters" style={{ width: '100%', display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
                    <div className="intra-filter-group" style={{ flex: '1', minWidth: '200px' }}>
                        <label className="intra-label" htmlFor="filtroTextoEncuestas">
                            Buscar
                        </label>
                        <input
                            id="filtroTextoEncuestas"
                            name="texto"
                            type="text"
                            className="intra-input"
                            value={filtros.texto}
                            onChange={handleFiltrosChange}
                            placeholder="Título o descripción..."
                        />
                    </div>
                    <div className="intra-filter-group" style={{ width: '150px' }}>
                        <label className="intra-label" htmlFor="filtroEstado">
                            Estado
                        </label>
                        <select
                            id="filtroEstado"
                            name="estado"
                            className="intra-select"
                            value={filtros.estado}
                            onChange={handleFiltrosChange}
                        >
                            <option value="todas">Todas</option>
                            <option value="activa">Activa</option>
                            <option value="cerrada">Cerrada</option>
                        </select>
                    </div>

                    <button
                        type="button"
                        className="btn-action"
                        onClick={() => { limpiarFormulario(); setShowAdminModal(true); }}
                        style={{
                            alignSelf: 'flex-end',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        + Nueva encuesta
                    </button>
                </div>
            </section>

            {/* LISTADO TIPO TABLA */}
            <section className="intra-news-layout" style={{ display: 'block' }}>
                <div className="intra-news-list-panel" style={{ width: '100%' }}>
                    <div className="intra-panel-header">
                        <h2 className="intra-panel-title">Listado de encuestas</h2>
                        <p className="intra-news-count">
                            {encuestasFiltradas.length} encuesta{encuestasFiltradas.length === 1 ? "" : "s"} encontrada{encuestasFiltradas.length === 1 ? "" : "s"}
                        </p>
                    </div>

                    {encuestasFiltradas.length === 0 ? (
                        <p className="intra-avisos-empty">No se encontraron encuestas con los filtros seleccionados.</p>
                    ) : (
                        <div className="intra-avisos-table-wrapper">
                            <table className="intra-avisos-table">
                                <thead>
                                    <tr>
                                        <th>Icono</th>
                                        <th>Nombre y Descripción</th>
                                        <th>Área</th>
                                        <th>Estado</th>
                                        <th>Resultados</th>
                                        <th>Editar</th>
                                        <th>Eliminar</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {encuestasFiltradas.map((enc) => {
                                        const area = areasNegocio.find(a => a.idAreaNegocio === enc.idAreaNegocio);
                                        return (
                                            <tr key={enc.idEncuesta}>
                                                <td style={{ width: '80px', padding: '0.5rem' }}>
                                                    <div style={{
                                                        width: '50px',
                                                        height: '50px',
                                                        backgroundColor: '#f1f5f9',
                                                        borderRadius: '10px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '1.5rem',
                                                        color: 'var(--color-primary)',
                                                        border: '1px solid #e2e8f0'
                                                    }}>
                                                        📝
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="intra-avisos-title-cell">
                                                        <span className="intra-avisos-title-main">{enc.nombre}</span>
                                                        <span className="intra-avisos-title-sub">{(enc.descripcion || '').slice(0, 80)}...</span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <span className="intra-avisos-meta" style={{ fontWeight: 600 }}>{area ? area.nombre : "Global"}</span>
                                                </td>
                                                <td>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                                                        <div
                                                            onClick={() => (enc.esOrigen || isSessionSuperAdmin()) && handleToggleActivo(enc)}
                                                            style={{
                                                                width: '38px',
                                                                height: '20px',
                                                                backgroundColor: enc.abierto ? '#10b981' : '#cbd5e1',
                                                                borderRadius: '12px',
                                                                position: 'relative',
                                                                cursor: (enc.esOrigen || isSessionSuperAdmin()) ? 'pointer' : 'not-allowed',
                                                                transition: 'background-color 0.2s',
                                                                flexShrink: 0,
                                                                opacity: (enc.esOrigen || isSessionSuperAdmin()) ? 1 : 0.5
                                                            }}
                                                            title={enc.abierto ? "Cerrar encuesta" : "Abrir encuesta"}
                                                        >
                                                            <div style={{
                                                                width: '14px',
                                                                height: '14px',
                                                                backgroundColor: 'white',
                                                                borderRadius: '50%',
                                                                position: 'absolute',
                                                                top: '3px',
                                                                left: enc.abierto ? '21px' : '3px',
                                                                transition: 'left 0.2s',
                                                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                                                            }} />
                                                        </div>
                                                        <span style={{
                                                            fontSize: '0.85rem',
                                                            fontWeight: '500',
                                                            color: enc.abierto ? '#10b981' : '#64748b',
                                                            minWidth: '60px'
                                                        }}>
                                                        </span>
                                                    </div>
                                                </td>
                                                <td>
                                                    <button
                                                        className="intra-button-ghost"
                                                        style={{ fontSize: '0.75rem', padding: '4px 10px' }}
                                                        onClick={() => handleSeleccionarEncuesta(enc)}
                                                    >
                                                        Ver resultados
                                                    </button>
                                                </td>
                                                <td>
                                                    {(enc.esOrigen || isSessionSuperAdmin()) && (
                                                        <button
                                                            type="button"
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                cursor: (enc.abierto || enc.tieneRespuestas) ? 'not-allowed' : 'pointer',
                                                                opacity: (enc.abierto || enc.tieneRespuestas) ? 0.3 : 1
                                                            }}
                                                            onClick={() => handleEditar(enc)}
                                                            title="Editar encuesta"
                                                        >
                                                            <img src={sigmaEdit} alt="Editar" style={{ width: '36px', height: 'auto' }} />
                                                        </button>
                                                    )}
                                                </td>
                                                <td>
                                                    {(enc.esOrigen || isSessionSuperAdmin()) && (
                                                        <button
                                                            type="button"
                                                            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                                                            onClick={() => handleEliminar(enc)}
                                                            title="Eliminar encuesta"
                                                        >
                                                            <img src={sigmaDelete} alt="Eliminar" style={{ width: '36px', height: 'auto' }} />
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </section>

            {/* MODAL DE RESULTADOS / ESTAD STICAS */}
            {
                showResultsModal && encuestaSeleccionada && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} onClick={() => setShowResultsModal(false)}>
                        <div style={{
                            backgroundColor: 'white', padding: '2.5rem', borderRadius: '16px',
                            width: '95%', maxWidth: '850px', maxHeight: '90vh', overflowY: 'auto',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                                <div>
                                    <h2 style={{ fontSize: '1.4rem', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                                        {encuestaSeleccionada.nombre}
                                    </h2>
                                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.1rem' }}>
                                        {encuestaSeleccionada.descripcion}
                                    </p>
                                </div>
                                <button onClick={() => setShowResultsModal(false)} style={{ border: 'none', background: 'transparent', fontSize: '2rem', cursor: 'pointer', color: '#94a3b8' }}>×</button>
                            </div>

                            {loadingStats ? (
                                <p style={{ padding: '2rem', textAlign: 'center', color: '#94a3b8' }}>Cargando estadísticas...</p>
                            ) : (
                                <div className="intra-survey-stats">
                                    {preguntasCargadas.map((p, idx) => {
                                        const stat = estadisticas[p.idEncuestaPregunta] || { total: 0 };
                                        const total = stat.total || 0;

                                        // Paleta de colores para las donas
                                        const coloresOM = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#f97316'];

                                        let datosDonut = [];
                                        if (p.tipo === "opcionMultiple" && stat.counts) {
                                            datosDonut = p.opciones.map((op, i) => ({
                                                label: op.texto,
                                                valor: stat.counts[op.idOpcion] || 0,
                                                color: coloresOM[i % coloresOM.length]
                                            }));
                                        } else if (p.tipo === "verdaderoFalso" && stat.counts) {
                                            datosDonut = [
                                                { label: "Verdadero", valor: stat.counts.V || 0, color: '#10b981' },
                                                { label: "Falso", valor: stat.counts.F || 0, color: '#ef4444' }
                                            ];
                                        }

                                        // Cálculos de SVG
                                        const cx = 80, cy = 80, r = 58, grosor = 18;
                                        const circunferencia = 2 * Math.PI * r;
                                        let offset = 0;
                                        const segs = datosDonut.map(d => {
                                            const pct = total === 0 ? 0 : d.valor / total;
                                            const dash = pct * circunferencia;
                                            const gap = circunferencia - dash;
                                            const rot = (offset / circunferencia) * 360 - 90;
                                            offset += dash;
                                            return { ...d, dash, gap, rot };
                                        });

                                        // Height calculation to prevent layout shift between bars and pie chart
                                        const chartHeight = Math.max(180, datosDonut.length * 44);

                                        return (
                                            <div key={p.idEncuestaPregunta} style={{
                                                display: 'grid',
                                                gridTemplateColumns: '60% 40%',
                                                gap: '2.5rem',
                                                marginBottom: '3rem',
                                                borderBottom: '1px solid #f1f5f9',
                                                paddingBottom: '2.5rem',
                                                alignItems: 'start'
                                            }}>

                                                {/* IZQUIERDA: GRÁFICAS Y LEYENDAS (60%) */}
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%', alignItems: 'center', overflow: 'hidden' }}>
                                                    {(p.tipo === "opcionMultiple" || p.tipo === "verdaderoFalso") ? (
                                                        <>
                                                            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                                {total > 0 && (
                                                                    <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', marginBottom: '1.5rem' }}>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setChartModes(prev => ({ ...prev, [p.idEncuestaPregunta]: chartModes[p.idEncuestaPregunta] === 'bars' ? 'circle' : 'bars' }))}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: '1px solid #e2e8f0',
                                                                                padding: '0.4rem 0.8rem',
                                                                                borderRadius: '6px',
                                                                                cursor: 'pointer',
                                                                                color: '#64748b',
                                                                                fontSize: '0.8rem',
                                                                                display: 'flex',
                                                                                alignItems: 'center',
                                                                                gap: '0.4rem',
                                                                                transition: 'background-color 0.2s',
                                                                            }}
                                                                            onMouseOver={(e) => e.target.style.backgroundColor = '#f1f5f9'}
                                                                            onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                                                                        >
                                                                            {chartModes[p.idEncuestaPregunta] === 'bars' ? (
                                                                                <>
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path><path d="M2 12h20"></path></svg>
                                                                                    Cambiar a gráfica circular
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="20" x2="18" y2="10"></line><line x1="12" y1="20" x2="12" y2="4"></line><line x1="6" y1="20" x2="6" y2="14"></line></svg>
                                                                                    Cambiar a gráfica de barras
                                                                                </>
                                                                            )}
                                                                        </button>
                                                                    </div>
                                                                )}
                                                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', width: '100%', minHeight: `${chartHeight}px` }}>
                                                                    {chartModes[p.idEncuestaPregunta] === 'bars' ? (
                                                                        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                                                            {datosDonut.map((opt, i) => {
                                                                                const count = opt.valor;
                                                                                const percent = total > 0 ? Math.round((count / total) * 100) : 0;
                                                                                return (
                                                                                    <div key={`${opt.label}_${i}`}>
                                                                                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem', marginBottom: '0.4rem', color: '#475569' }}>
                                                                                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                                                <span style={{ width: '8px', height: '8px', backgroundColor: opt.color, borderRadius: '50%', display: 'inline-block' }}></span>
                                                                                                {opt.label}
                                                                                            </span>
                                                                                            <span style={{ fontWeight: 600 }}>{percent}%</span>
                                                                                        </div>
                                                                                        <div style={{ width: '100%', height: '10px', backgroundColor: '#f1f5f9', borderRadius: '5px', overflow: 'hidden' }}>
                                                                                            <div style={{ width: `${percent}%`, height: '100%', backgroundColor: opt.color, borderRadius: '5px' }}></div>
                                                                                        </div>
                                                                                    </div>
                                                                                );
                                                                            })}
                                                                        </div>
                                                                    ) : (
                                                                        <svg viewBox="0 0 160 160" width="180" height="180">
                                                                            <circle cx={cx} cy={cy} r={r} fill="none" stroke="#e2e8f0" strokeWidth={grosor} />
                                                                            {total > 0 && segs.map((s, i) => s.dash > 0.5 && (
                                                                                <circle
                                                                                    key={i}
                                                                                    cx={cx} cy={cy} r={r}
                                                                                    fill="none"
                                                                                    stroke={s.color}
                                                                                    strokeWidth={grosor}
                                                                                    strokeDasharray={`${s.dash.toFixed(2)} ${s.gap.toFixed(2)}`}
                                                                                    strokeDashoffset={0}
                                                                                    transform={`rotate(${s.rot.toFixed(2)} ${cx} ${cy})`}
                                                                                    strokeLinecap="butt"
                                                                                />
                                                                            ))}
                                                                            <text x={cx} y={cy - 2} textAnchor="middle" fontSize="28" fontWeight="800" fill="#1e293b">{total}</text>
                                                                            <text x={cx} y={cy + 18} textAnchor="middle" fontSize="11" fill="#94a3b8">respuestas</text>
                                                                        </svg>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            {/* Render de la Leyenda */}
                                                            <div style={{ width: '100%', display: 'flex', paddingBottom: '0.5rem', paddingTop: '1rem', overflow: 'hidden' }}>
                                                                <div className="custom-scrollbar" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: '0.85rem', overflowX: 'auto', paddingBottom: '0.5rem', width: '100%', margin: '0 1rem' }}>
                                                                    {datosDonut.map((opt, i) => {
                                                                        const count = opt.valor;
                                                                        return (
                                                                            <div key={`${opt.label}_${i}`} style={{ display: 'flex', alignItems: 'center', gap: '0.85rem' }}>
                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.9rem', color: '#475569', whiteSpace: 'nowrap' }}>
                                                                                    <span style={{ width: '10px', height: '10px', backgroundColor: opt.color, borderRadius: '50%', display: 'inline-block', flexShrink: 0 }}></span>
                                                                                    <span>{opt.label} ({count})</span>
                                                                                </div>
                                                                                {i < datosDonut.length - 1 && (
                                                                                    <span style={{ color: '#cbd5e1' }}>|</span>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            </div>
                                                        </>
                                                    ) : p.tipo === "textoLibre" ? (
                                                        <div style={{
                                                            flex: 1,
                                                            width: '100%',
                                                            maxHeight: '220px',
                                                            overflowY: 'auto',
                                                            backgroundColor: '#f8fafc',
                                                            borderRadius: '8px',
                                                            border: '1px solid #e2e8f0',
                                                            padding: '1rem',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: '0.5rem'
                                                        }}>
                                                            {stat.textos && stat.textos.length > 0 ? (
                                                                stat.textos.map((txt, i) => (
                                                                    <div key={i} style={{ padding: '0.6rem', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '0.85rem', color: '#334155', lineHeight: 1.4 }}>
                                                                        {txt}
                                                                    </div>
                                                                ))
                                                            ) : (
                                                                <div style={{ color: '#94a3b8', fontSize: '0.85rem', textAlign: 'center', marginTop: '1rem' }}>No hay respuestas de texto.</div>
                                                            )}
                                                        </div>
                                                    ) : null}
                                                </div>

                                                {/* DERECHA: PREGUNTA (25%) */}
                                                <div style={{ paddingTop: '0.5rem' }}>
                                                    <h3 style={{ fontWeight: 600, color: '#1e293b', margin: '0 0 0.25rem 0', fontSize: '1.1rem' }}>
                                                        {idx + 1}. {p.descripcionPregunta}
                                                    </h3>
                                                    <p style={{ fontWeight: 400, color: '#64748b', fontSize: '0.85rem', margin: '0 0 1.5rem 0' }}>
                                                        {total} respuesta{total !== 1 ? 's' : ''} en total
                                                    </p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                )
            }

            {/* GESTOR DE ENCUESTAS (MODAL) */}
            {
                showAdminModal && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} onClick={() => setShowAdminModal(false)}>
                        <div style={{
                            backgroundColor: 'white', padding: '1.25rem 2rem', borderRadius: '16px',
                            width: '95%', maxWidth: '850px', maxHeight: '90vh', overflowY: 'auto',
                            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)'
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <div>
                                    <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0, color: '#1e293b' }}>
                                        {editingId ? "Editar encuesta" : "Gestor de encuestas"}
                                    </h2>
                                    <p style={{ fontSize: '0.85rem', color: '#64748b', marginTop: '0.1rem', marginBottom: 0 }}>
                                        {editingId ? "Modifica los datos y preguntas de la encuesta seleccionada." : "Crea encuestas definiendo nombre, descripción y preguntas."}
                                    </p>
                                </div>
                                <button onClick={() => { setShowAdminModal(false); setEditingId(null); }} style={{ border: 'none', background: 'transparent', fontSize: '2rem', cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
                            </div>

                            <form
                                className="intra-avisos-form intra-survey-admin-form"
                                onSubmit={handleCrearEncuesta}
                            >
                                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '0.75rem' }}>
                                    <div className="intra-form-group" style={{ flex: 1.5 }}>
                                        <label className="intra-label" htmlFor="nombreNuevaEncuesta" style={{ marginBottom: '4px' }}>
                                            Nombre
                                        </label>
                                        <input
                                            id="nombreNuevaEncuesta"
                                            name="nombre"
                                            type="text"
                                            className={`intra-input ${erroresNueva.nombre ? "intra-input-error" : ""}`}
                                            value={nuevaEncuesta.nombre}
                                            onChange={handleNuevaEncuestaChange}
                                            placeholder="Ej. Evaluación de capacitación de inducción"
                                        />
                                        {erroresNueva.nombre && (
                                            <p className="intra-error-text">{erroresNueva.nombre}</p>
                                        )}
                                    </div>

                                    <div className="intra-form-group" style={{ flex: 1 }}>
                                        <label className="intra-label" htmlFor="areaNuevaEncuesta" style={{ marginBottom: '4px' }}>
                                            Área responsable
                                        </label>
                                        <select
                                            id="areaNuevaEncuesta"
                                            name="idAreaNegocio"
                                            className="intra-select"
                                            value={nuevaEncuesta.idAreaNegocio || ""}
                                            onChange={handleNuevaEncuestaChange}
                                        >
                                            {areasNegocio.map((area) => (
                                                <option key={area.idAreaNegocio} value={area.idAreaNegocio}>
                                                    {area.nombre}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="intra-form-group" style={{ marginBottom: '1rem' }}>
                                    <label
                                        className="intra-label"
                                        htmlFor="descripcionNuevaEncuesta"
                                        style={{ marginBottom: '4px' }}
                                    >
                                        Descripción (opcional)
                                    </label>
                                    <input
                                        id="descripcionNuevaEncuesta"
                                        name="descripcion"
                                        type="text"
                                        className={`intra-input ${erroresNueva.descripcion ? "intra-input-error" : ""}`}
                                        value={nuevaEncuesta.descripcion}
                                        onChange={handleNuevaEncuestaChange}
                                        placeholder="Describe a quién va dirigida y qué quieres medir."
                                    />
                                    {erroresNueva.descripcion && (
                                        <p className="intra-error-text">
                                            {erroresNueva.descripcion}
                                        </p>
                                    )}
                                </div>

                                <div style={{ display: 'flex', gap: '2rem', height: '100%', minHeight: '350px' }}>
                                    {/* LEFT PANE: EDITOR */}
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '1rem', borderRight: '1px solid #e2e8f0', paddingRight: '2rem' }}>
                                        <p style={{ fontSize: '0.95rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                                            Configuración de pregunta {selectedQuestionIndex + 1}
                                        </p>

                                        {nuevasPreguntas[selectedQuestionIndex] && (
                                            <div className="intra-survey-new-question-row" style={{ padding: 0, border: 'none', background: 'none' }}>
                                                <div className="intra-form-group" style={{ marginBottom: '1.25rem' }}>
                                                    <label className="intra-label" style={{ marginBottom: '4px' }}>Tipo de Pregunta</label>
                                                    <div style={{
                                                        display: 'flex',
                                                        backgroundColor: '#f1f5f9',
                                                        borderRadius: '10px',
                                                        padding: '4px',
                                                        border: '1px solid #e2e8f0',
                                                        position: 'relative',
                                                        cursor: 'pointer',
                                                        height: '42px',
                                                        userSelect: 'none'
                                                    }}>
                                                        {/* Sliding Indicator */}
                                                        <div style={{
                                                            position: 'absolute',
                                                            top: '4px',
                                                            bottom: '4px',
                                                            left: '4px',
                                                            width: 'calc(50% - 4px)',
                                                            backgroundColor: '#ffffff',
                                                            borderRadius: '6px',
                                                            boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                                            transform: nuevasPreguntas[selectedQuestionIndex].tipo === 'opcionMultiple' ? 'translateX(0)' : 'translateX(100%)',
                                                            transition: 'transform 0.3s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                                            zIndex: 0
                                                        }} />

                                                        {/* Option: Multiple Choice */}
                                                        <div
                                                            onClick={() => handlePreguntaChange(nuevasPreguntas[selectedQuestionIndex].id, "tipo", "opcionMultiple")}
                                                            style={{
                                                                flex: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                                zIndex: 1,
                                                                fontSize: '0.85rem',
                                                                fontWeight: nuevasPreguntas[selectedQuestionIndex].tipo === 'opcionMultiple' ? 700 : 500,
                                                                color: nuevasPreguntas[selectedQuestionIndex].tipo === 'opcionMultiple' ? 'var(--color-primary)' : '#64748b',
                                                                transition: 'color 0.2s'
                                                            }}
                                                        >
                                                            Opción múltiple
                                                        </div>

                                                        {/* Option: Free Text */}
                                                        <div
                                                            onClick={() => handlePreguntaChange(nuevasPreguntas[selectedQuestionIndex].id, "tipo", "textoLibre")}
                                                            style={{
                                                                flex: 1,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                gap: '8px',
                                                                zIndex: 1,
                                                                fontSize: '0.85rem',
                                                                fontWeight: nuevasPreguntas[selectedQuestionIndex].tipo === 'textoLibre' ? 700 : 500,
                                                                color: nuevasPreguntas[selectedQuestionIndex].tipo === 'textoLibre' ? 'var(--color-primary)' : '#64748b',
                                                                transition: 'color 0.2s'
                                                            }}
                                                        >
                                                            Texto libre
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="intra-form-group" style={{ marginBottom: '1rem' }}>
                                                    <label className="intra-label" style={{ marginBottom: '4px' }}>Pregunta</label>
                                                    <input
                                                        type="text"
                                                        className={`intra-input ${getQuestionError(nuevasPreguntas[selectedQuestionIndex]) ? "intra-input-error" : ""}`}
                                                        placeholder="Escribe el texto de la pregunta..."
                                                        value={nuevasPreguntas[selectedQuestionIndex].texto}
                                                        onChange={(e) =>
                                                            handlePreguntaChange(nuevasPreguntas[selectedQuestionIndex].id, "texto", e.target.value)
                                                        }
                                                    />
                                                    {getQuestionError(nuevasPreguntas[selectedQuestionIndex]) && (
                                                        <p className="intra-error-text">
                                                            {getQuestionError(nuevasPreguntas[selectedQuestionIndex])}
                                                        </p>
                                                    )}
                                                </div>

                                                {nuevasPreguntas[selectedQuestionIndex].tipo === "opcionMultiple" && (
                                                    <div className="intra-survey-options-editor" style={{ marginTop: '0.75rem' }}>
                                                        <label className="intra-label" style={{ marginLeft: '40px', marginBottom: '4px', fontSize: '0.8rem', opacity: 0.8 }}>Opciones</label>
                                                        <div
                                                            className="custom-scrollbar"
                                                            style={{
                                                                maxHeight: '125px',
                                                                overflowY: 'auto',
                                                                paddingRight: '6px'
                                                            }}
                                                        >
                                                            {nuevasPreguntas[selectedQuestionIndex].opciones.map((opcion, opIdx) => (
                                                                <div key={opIdx} className="intra-survey-option-row" style={{ display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '4px' }}>
                                                                    <button
                                                                        type="button"
                                                                        className={`intra-survey-option-btn ${(nuevasPreguntas[selectedQuestionIndex].opciones.length > 3 && (opIdx < nuevasPreguntas[selectedQuestionIndex].opciones.length - 1 || (opIdx === 9 && opcion.trim() !== ""))) ? 'is-deletable' : ''}`}
                                                                        data-letter={String.fromCharCode(65 + opIdx)}
                                                                        onClick={() => handleEliminarOpcion(nuevasPreguntas[selectedQuestionIndex].id, opIdx)}
                                                                        style={{
                                                                            width: '28px',
                                                                            height: '28px',
                                                                            minWidth: '28px',
                                                                            fontSize: '0.75rem',
                                                                            visibility: (opcion.trim() !== "" || opIdx < nuevasPreguntas[selectedQuestionIndex].opciones.length - 1) ? 'visible' : 'hidden'
                                                                        }}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        className="intra-input"
                                                                        style={{ flex: 1, height: '34px', fontSize: '0.85rem' }}
                                                                        placeholder={`Opción ${opIdx + 1}`}
                                                                        value={opcion}
                                                                        onChange={(e) =>
                                                                            handleOpcionChange(nuevasPreguntas[selectedQuestionIndex].id, opIdx, e.target.value)
                                                                        }
                                                                        onBlur={() => handleOpcionBlur(nuevasPreguntas[selectedQuestionIndex].id)}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </div>
                                                        {/* No es necesario el bot n de agregar: es autom tico al escribir en la  ltima */}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* RIGHT PANE: PREVIEW & SELECTION */}
                                    <div style={{ flex: 1.2, display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <p style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
                                                Vista previa de preguntas
                                            </p>
                                            <button
                                                type="button"
                                                className={`intra-survey-add-question-btn ${!canAddQuestion ? 'is-disabled' : ''}`}
                                                disabled={!canAddQuestion}
                                                onClick={() => {
                                                    const newId = Date.now();
                                                    setNuevasPreguntas([...nuevasPreguntas, { id: newId, tipo: "opcionMultiple", texto: "", opciones: ["", "", ""] }]);
                                                    setSelectedQuestionIndex(nuevasPreguntas.length);
                                                }}
                                                style={{
                                                    opacity: canAddQuestion ? 1 : 0.5,
                                                    cursor: canAddQuestion ? 'pointer' : 'not-allowed'
                                                }}
                                            >
                                                + Nueva pregunta
                                            </button>
                                        </div>

                                        {erroresNueva.preguntas && (
                                            <p className="intra-error-text" style={{ margin: 0 }}>
                                                {erroresNueva.preguntas}
                                            </p>
                                        )}

                                            <div
                                            className="intra-survey-list"
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '1rem',
                                                overflowY: 'auto',
                                                maxHeight: '320px',
                                                paddingRight: '6px'
                                            }}
                                        >
                                            {nuevasPreguntas.map((p, idx) => (
                                                <div
                                                    key={p.id}
                                                    onClick={() => setSelectedQuestionIndex(idx)}
                                                    style={{
                                                        padding: '1.25rem',
                                                        borderRadius: '12px',
                                                        border: '2px solid',
                                                        borderColor: selectedQuestionIndex === idx ? 'var(--color-primary)' : '#f1f5f9',
                                                        backgroundColor: selectedQuestionIndex === idx ? 'var(--color-primary-100, #FFE4EC)' : 'white',
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s ease',
                                                        position: 'relative'
                                                    }}
                                                >
                                                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                                                        <span style={{ fontWeight: 700, color: selectedQuestionIndex === idx ? 'var(--color-primary)' : '#94a3b8' }}>{idx + 1}.</span>
                                                        <div style={{ flex: 1 }}>
                                                            <p style={{
                                                                margin: '0 0 8px 0',
                                                                fontSize: '0.95rem',
                                                                fontWeight: 600,
                                                                color: '#334155',
                                                                wordBreak: 'break-word',
                                                                overflowWrap: 'anywhere'
                                                            }}>
                                                                {p.texto || <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>Pregunta sin título</span>}
                                                            </p>

                                                            {/* Render small preview of options, excluding empty ghost options */}
                                                            {p.tipo === "opcionMultiple" && (
                                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                                    {p.opciones.filter((opValue, opI) => opValue.trim() !== "" || opI < p.opciones.length - 1).map((op, i) => (
                                                                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                                            <div style={{ width: '14px', height: '14px', borderRadius: '50%', border: '1.5px solid #cbd5e1' }} />
                                                                            <span style={{ fontSize: '0.85rem', color: op.trim() === "" ? '#cbd5e1' : '#64748b', fontStyle: op.trim() === "" ? 'italic' : 'normal' }}>
                                                                                {op || `Opción ${i + 1}`}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {p.tipo === "textoLibre" && (
                                                                <div style={{ height: '32px', width: '100%', border: '1px solid #e2e8f0', borderRadius: '6px', backgroundColor: '#f8fafc', padding: '6px 12px', fontSize: '0.8rem', color: '#94a3b8' }}>
                                                                    Respuesta de texto...
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {nuevasPreguntas.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleEliminarPregunta(p.id);
                                                                if (selectedQuestionIndex >= idx && selectedQuestionIndex > 0) {
                                                                    setSelectedQuestionIndex(prev => prev - 1);
                                                                }
                                                            }}
                                                            style={{
                                                                position: 'absolute', top: '10px', right: '10px',
                                                                background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.2rem'
                                                            }}
                                                        >

                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>

                                <div className="intra-form-row intra-form-row-between" style={{ marginTop: '2rem' }}>
                                    <button type="submit" className="intra-button-primary" disabled={isUploading}>
                                        {isUploading ? "Guardando..." : editingId ? "Guardar cambios" : "Crear encuesta"}
                                    </button>
                                    <button
                                        type="button"
                                        className="intra-button-ghost"
                                        onClick={() => { setShowAdminModal(false); setEditingId(null); }}
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )
            }

            {/* MODAL COMPARTIR */}
            {showShareModal && (
                <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1050, padding: '2rem' }}>
                    <div style={{ width: '100%', maxWidth: '500px', backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ padding: '1.5rem', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: '#f8fafc' }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>Compartir Encuesta</h2>
                            <button type="button" onClick={() => { setShowShareModal(false); setShowAdminModal(true); setIsUploading(false); }} style={{ background: 'none', border: 'none', fontSize: '2rem', color: '#64748b', cursor: 'pointer' }}>×</button>
                        </div>
                        <div style={{ padding: '1.5rem', overflowY: 'auto', maxHeight: '60vh' }}>
                            <p style={{ marginBottom: '1.5rem', color: '#475569' }}>Seleccione las empresas con las que desea compartir esta encuesta:</p>
                            {descendantCompanies.length === 0 ? (
                                <p style={{ color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>No hay empresas hijas disponibles.</p>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {descendantCompanies.map(comp => {
                                        const cId = comp.IdEmpresa || comp.idEmpresa;
                                        const cName = comp.Nombre || comp.nombre;
                                        const cParent = comp.NombrePadre || comp.nombrePadre || 'N/A';
                                        return (
                                            <div key={cId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.75rem', backgroundColor: '#f1f5f9', borderRadius: '8px' }}>
                                                <div>
                                                    <div style={{ fontWeight: 'bold', color: '#1e293b' }}>{cName}</div>
                                                    <div style={{ fontSize: '0.85rem', color: '#64748b' }}>({cParent})</div>
                                                </div>
                                                <div>
                                                    <label style={{ position: 'relative', display: 'inline-block', width: '44px', height: '24px' }}>
                                                        <input
                                                            type="checkbox"
                                                            checked={!!selectedCompanies[cId]}
                                                            onChange={(e) => setSelectedCompanies(prev => ({ ...prev, [cId]: e.target.checked }))}
                                                            style={{ opacity: 0, width: 0, height: 0 }}
                                                        />
                                                        <span style={{
                                                            position: 'absolute', cursor: 'pointer', top: 0, left: 0, right: 0, bottom: 0,
                                                            backgroundColor: selectedCompanies[cId] ? '#10b981' : '#cbd5e1',
                                                            transition: '.4s', borderRadius: '24px'
                                                        }}>
                                                            <span style={{
                                                                position: 'absolute', height: '18px', width: '18px', left: '3px', bottom: '3px',
                                                                backgroundColor: 'white', transition: '.4s', borderRadius: '50%',
                                                                transform: selectedCompanies[cId] ? 'translateX(20px)' : 'translateX(0)'
                                                            }} />
                                                        </span>
                                                    </label>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                        <div style={{ padding: '1.5rem', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                            <button
                                type="button"
                                onClick={() => { setShowShareModal(false); setShowAdminModal(true); setIsUploading(false); }}
                                className="intra-button-ghost"
                            >
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={() => executeGuardarComparticion(createdEncuestaId, selectedCompanies)}
                                className="intra-button-primary"
                                disabled={sharingLoading}
                                style={{ opacity: sharingLoading ? 0.7 : 1, backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)' }}
                            >
                                {sharingLoading ? 'Guardando...' : 'Confirmar y Guardar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL RESPUESTAS TEXTO */}
            {
                modalTextoOpen && (
                    <div style={{
                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                        backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }} onClick={() => setModalTextoOpen(false)}>
                        <div style={{
                            backgroundColor: 'white', padding: '2rem', borderRadius: '12px',
                            width: '90%', maxWidth: '600px', maxHeight: '80vh', overflowY: 'auto'
                        }} onClick={e => e.stopPropagation()}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ fontSize: '1.25rem', fontWeight: 600 }}>{modalTextoTitulo}</h3>
                                <button onClick={() => setModalTextoOpen(false)} style={{ border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer' }}> </button>
                            </div>
                            {modalTextoLista.length === 0 ? (
                                <p style={{ color: '#94a3b8' }}>No hay respuestas de texto.</p>
                            ) : (
                                <ul style={{ listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                                    {modalTextoLista.map((t, i) => (
                                        <li key={i} style={{ padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                            "{t}"
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                )
            }

            {/* Alerta de confirmación/error */}
            <AlertDialog
                open={alertDialog.open}
                title={alertDialog.title}
                message={alertDialog.message}
                variant={alertDialog.variant}
                onClose={() => setAlertDialog(prev => ({ ...prev, open: false }))}
            />

            <ConfirmDeleteDialog
                open={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, item: null })}
                onConfirm={executeDelete}
                itemTitle={deleteDialog.item?.nombre}
            />
        </div>
    );
};

export default Encuestas;
